import { DatabaseSoundPackModel } from '../services/DatabaseSoundPack';

// Segments above this estimated bitrate have been confirmed to exceed
// WKWebView's MediaSource SourceBuffer quota on macOS, causing the plugin's
// video preview to silently fail (video.js/VHS never surfaces a real error —
// see the sample-pack-video-encode skill for the full investigation). This
// sits comfortably above the ~2.5-5 Mbps that profile targets, so it only
// fires for genuinely oversized exports.
const BITRATE_WARNING_THRESHOLD_BPS = 6_000_000;

export type HlsValidation = { valid: boolean; error?: string; bitrateWarning?: string };

/**
 * Checks a selected HLS folder: a parseable .m3u8 playlist, every segment it
 * references present, and (as a warning only) an estimated bitrate the plugin's
 * embedded player can handle.
 */
export async function validateHLSFiles(files: File[]): Promise<HlsValidation> {
    // Find the .m3u8 playlist file
    const playlistFile = files.find(file => file.name.endsWith('.m3u8'));

    if (!playlistFile) {
        return { valid: false, error: 'No .m3u8 playlist file found in video folder.' };
    }

    // Read the playlist file content
    const playlistContent = await playlistFile.text();
    const lines = playlistContent.split('\n');

    // Pair each #EXTINF duration with the segment filename on the following line.
    const segments: { filename: string; durationSecs: number }[] = [];
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line.startsWith('#EXTINF:')) continue;

        const durationSecs = parseFloat(line.slice('#EXTINF:'.length));
        // Extract just the filename (in case playlist has paths like "./segment.ts")
        const segmentFilename = lines[i + 1]?.trim().split('/').pop()?.trim();

        if (segmentFilename && !isNaN(durationSecs)) {
            segments.push({ filename: segmentFilename, durationSecs });
        }
    }

    if (segments.length === 0) {
        return { valid: false, error: 'Playlist file is empty or invalid.' };
    }

    // Get uploaded files by name
    const filesByName = new Map(files.map(f => [f.name, f]));

    // Check if all required segments are present
    const missingSegments = segments
        .map(s => s.filename)
        .filter(filename => !filesByName.has(filename));

    if (missingSegments.length > 0) {
        return {
            valid: false,
            error: `Missing ${missingSegments.length} segment file(s): ${missingSegments.slice(0, 3).join(', ')}${missingSegments.length > 3 ? '...' : ''}`
        };
    }

    // Estimate overall bitrate from segment file sizes vs. their playlist
    // durations, to catch high-bitrate sources before they reach production.
    const totalBytes = segments.reduce((sum, s) => sum + (filesByName.get(s.filename)?.size ?? 0), 0);
    const totalDurationSecs = segments.reduce((sum, s) => sum + s.durationSecs, 0);
    const estimatedBitrateBps = totalDurationSecs > 0 ? (totalBytes * 8) / totalDurationSecs : 0;

    let bitrateWarning: string | undefined;
    if (estimatedBitrateBps > BITRATE_WARNING_THRESHOLD_BPS) {
        const mbps = (estimatedBitrateBps / 1_000_000).toFixed(1);
        bitrateWarning = `Estimated bitrate is ~${mbps} Mbps, which may fail to play in the plugin's embedded preview on macOS. Consider re-encoding first (resolution ≤720p, ~5 Mbps cap) — the "sample-pack-video-encode" tool does this for you.`;
    }

    return { valid: true, bitrateWarning };
}

/**
 * Rewrites the playlist's segment references to the pack's Cloud Storage
 * download paths, returning the file list with the edited playlist swapped in.
 */
export async function editManifestFile(files: File[], packId: string): Promise<File[]> {
    const playlistFile = files.find(file => file.name.endsWith('.m3u8'));

    if (!playlistFile) {
        return files;
    }

    // Read the original playlist content
    const playlistContent = await playlistFile.text();

    // Edit each line
    const editedLines = playlistContent.split('\n').map(line => {
        // Only modify non-comment lines (segment references)
        if (line.trim() && !line.startsWith('#')) {
            // Extract just the filename
            const filename = line.split('/').pop()?.trim() || line.trim();
            // Add prefix and suffix
            return `${DatabaseSoundPackModel.COLLECTION_NAME}%2F${packId}%2Fvideo%2F${filename}?alt=media`;
        }
        return line;
    });

    // Create a new File object with the edited content
    const editedPlaylistFile = new File(
        [editedLines.join('\n')],
        playlistFile.name,
        { type: playlistFile.type }
    );

    // Return new array with edited playlist file
    return files.map(file =>
        file === playlistFile ? editedPlaylistFile : file
    );
}
